import { Button, Empty, Popup, Tabs } from 'antd-mobile';

import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import {
  addEnergyPlan,
  EnergyConsumptionAdminList,
  EnergyConsumptionPlanGetStaffOrgMapping,
  getEnergyConsumptionAdminList,
  getPlanDetail,
  getStaffOrgMapping,
  getUsedTime,
  MediumInfo,
  TimeDetailItem,
  updateEnergyPlan,
} from '@/api/energy';
import BatchConfiguration from './components/BatchConfiguration';
import ConfigurationTime from './components/ConfigurationTime';
import { formatTimeRange, generateDateList, getCurrentCycleStart, getWeekRange } from './util';
import { isEmpty, mergeWith } from 'lodash-es';
import NoData from '@/assets/images/noData.png';
import { useNavigate, useParams } from 'react-router-dom';
import DateRange from './components/DateRange';

export enum OperationType {
  BatchClear = 1,
  BatchConfig = 2,
}

// 定义日期对象类型
export interface DateItem {
  date: string;
  detailList?: TimeDetailItem[];
}

export type ConfigArea = Partial<{
  areaId: string;
  mediumId: string;
  date: string;
  detailList: TimeDetailItem[];
  orgInfo: { id: string; name: string };
}>;

type AreaMediumDateDetailMap = {
  [mediumId: string]: {
    [areaId: string]: {
      [date: string]: TimeDetailItem[];
    };
  };
};

const arrayToMap = (data: any) => {
  const map: {
    [mediumId: string]: {
      [areaId: string]: {
        [date: string]: TimeDetailItem[];
      };
    };
  } = {};
  data!.forEach((medium: any) => {
    const mediumId = String(medium.mediumId);
    if (!map[mediumId]) map[mediumId] = {};
    medium.ownerOrgInfoVoList[0]?.areaInfoVoList.forEach((area: any) => {
      const areaId = String(area.areaId);
      if (!map[mediumId][areaId]) map[mediumId][areaId] = {};
      area.timeDetailVoList.forEach((dateItem: any) => {
        map[mediumId][areaId][dateItem.date] = dateItem.detailList || [];
      });
    });
  });

  return map;
};

function mapToArray(
  map: AreaMediumDateDetailMap,
  mediumList: EnergyConsumptionAdminList[] | undefined,
  areaList: EnergyConsumptionPlanGetStaffOrgMapping['areaList'] | undefined,
  orgInfo: { id: string; name: string } | undefined
) {
  const result: any[] = [];
  Object.entries(map).forEach(([mediumId, areaObj]) => {
    const medium = mediumList?.find(m => String(m.energyMediumId) === mediumId);
    const mediumName = medium?.energyMediumName || '';
    const ownerOrgInfoVoList: any[] = [
      {
        ownerOrgId: orgInfo?.id || '',
        ownerOrgName: orgInfo?.name || '',
        areaInfoVoList: Object.entries(areaObj).map(([areaId, dateObj]) => {
          const area = areaList?.find(a => Number(a!.id) === Number(areaId));
          return {
            areaId: Number(areaId),
            areaName: area?.zoneName || '',
            timeDetailVoList: Object.entries(dateObj).map(([date, detailList]) => ({
              date,
              displayType: 1,
              detailList,
            })),
          };
        }),
      },
    ];
    result.push({
      mediumId: Number(mediumId),
      mediumName,
      ownerOrgInfoVoList,
    });
  });
  return result;
}

const EnergyUsePlan = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [timeRange, setTimeRange] = useState<[dayjs.Dayjs, dayjs.Dayjs | null]>([dayjs(), null]);
  const [activeTab, setActiveTab] = useState<string>();
  const [mediumList, setMediumList] = useState<EnergyConsumptionAdminList[]>();
  const [areaList, setAreaList] = useState<EnergyConsumptionPlanGetStaffOrgMapping['areaList']>();
  const [orgInfo, setOrgInfo] = useState<{ id: string; name: string }>();
  const [dateList, setDateList] = useState<DateItem[]>([]);
  const [showConfigDatePopup, setShowConfigDatePopup] = useState(false);
  const [showBatchPopup, setShowBatchPopup] = useState(false);
  const [operationType, setOperationType] = useState<OperationType>();
  const [disabledTimes, setDisabledTimes] = useState<Array<string>>([]);
  const [limitTime, setLimitTime] = useState<boolean>();

  const conflictedTime = useRef<Map<string, any>>(new Map()); //存储冲突的时间段
  // 新增：能源介质-区域-日期的时间段存储结构
  const [areaMediumDateDetailMap, setAreaMediumDateDetailMap] = useState<AreaMediumDateDetailMap>({});

  // 当前选中的区域和日期（用于配置弹窗）
  const [currentConfigArea, setCurrentConfigArea] = useState<ConfigArea | null>(null);

  useEffect(() => {
    document.title = '用能计划';
  }, []);

  // 当时间范围变化时，重新生成日期列表
  useEffect(() => {
    if (timeRange[0] && timeRange[1]) {
      const dates = generateDateList(timeRange!);
      setDateList(dates);
    }
  }, [timeRange]);

  useEffect(() => {
    getEnergyConsumptionAdminList().then(res => {
      setMediumList(res.list ?? []);
      if (res.list) {
        setActiveTab(String(res.list[0].energyMediumId || 0));
      }
    });
  }, []);

  useEffect(() => {
    getStaffOrgMapping().then(res => {
      console.log('getStaffOrgMapping', res);
      setLimitTime(res.limitTime);
      setAreaList(res.areaList ?? []);
      setOrgInfo({ id: res.ownerOrgId!, name: res.ownerOrgName! });
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(areaList) && !isEmpty(mediumList) && !id) {
      const defaultAreaMediumDateDetailMap: AreaMediumDateDetailMap = {};
      mediumList!.forEach(medium => {
        defaultAreaMediumDateDetailMap[medium.energyMediumId!] = {};

        areaList?.forEach(area => {
          defaultAreaMediumDateDetailMap[medium.energyMediumId!][area.id!] = {};

          dateList.forEach(date => {
            defaultAreaMediumDateDetailMap[medium.energyMediumId!][area.id!][date.date] = date.detailList || [];
          });
        });
      });

      setAreaMediumDateDetailMap(defaultAreaMediumDateDetailMap);
    }
  }, [areaList, mediumList, dateList]);

  useEffect(() => {
    if (!id) {
      // 默认选择本周期
      const [start, end] = getWeekRange(getCurrentCycleStart());
      setTimeRange([start, end]);
    } else {
      getPlanDetail({ id: id! }).then(res => {
        if (!isEmpty(res.energyConsumptionPlanOfMediumList)) {
          const data = res.energyConsumptionPlanOfMediumList;
          const detailMap = arrayToMap(data);
          setAreaMediumDateDetailMap(detailMap);
          setTimeRange([
            dayjs(data?.[0]?.ownerOrgInfoVoList?.[0]?.areaInfoVoList?.[0]?.timeDetailVoList?.[0]?.date!),
            dayjs(data?.[0]?.ownerOrgInfoVoList?.[0]?.areaInfoVoList?.[0]?.timeDetailVoList?.[6]?.date!),
          ]);
        }
      });
    }
  }, [id]);

  useEffect(() => {
    if (!isEmpty(mediumList) && !isEmpty(areaList) && !isEmpty(dateList)) {
      getUsedTime({
        //data去除detailList
        energyConsumptionPlanOfMediumList: mediumList!.map((medium: any) => ({
          mediumId: medium.energyMediumId,
          mediumName: medium.energyMediumName,
          ownerOrgInfoVoList: [
            {
              ownerOrgId: orgInfo?.id!,
              ownerOrgName: orgInfo?.name!,
              areaInfoVoList: areaList!.map((area: any) => ({
                areaId: area.id!,
                areaName: area.zoneName!,
                timeDetailVoList: dateList.map(date => date.date),
              })),
            },
          ],
        })),
        id: id ? Number(id) : undefined,
      }).then((res: any) => {
        res.energyConsumptionPlanOfMediumList.forEach((medium: MediumInfo) => {
          medium.ownerOrgInfoVoList.forEach(ownerOrgInfo => {
            ownerOrgInfo.areaInfoVoList.forEach(area => {
              area.timeDetailVoList.forEach(date => {
                conflictedTime.current.set(
                  `${medium.mediumId}/${ownerOrgInfo.ownerOrgId}/${area.areaId}/${date.date}`,
                  date.detailList?.map(item => formatTimeRange(date.date, item.startDateTime, item.endDateTime))
                );
              });
            });
          });
        });
      });
    }
  }, [mediumList, areaList, dateList]);

  // 处理标签页切换
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  // 配置时间弹窗确认
  const handleConfigTimeOk = (detailList: TimeDetailItem[]) => {
    if (!currentConfigArea) return;
    const { mediumId, areaId, date } = currentConfigArea;
    setAreaMediumDateDetailMap(prev => {
      const newMap = { ...prev };
      if (!newMap[mediumId!]) newMap[mediumId!] = {};
      if (!newMap[mediumId!][areaId!]) newMap[mediumId!][areaId!] = {};
      newMap[mediumId!][areaId!][date!] = detailList;
      return newMap;
    });
    setShowConfigDatePopup(false);
  };

  const handleSubmit = () => {
    const energyConsumptionPlanOfMediumList = mapToArray(areaMediumDateDetailMap, mediumList, areaList, orgInfo);
    // 保存的时候需要展示保存秒，虽然秒并没有用
    const api = id ? updateEnergyPlan : addEnergyPlan;
    api({ energyConsumptionPlanOfMediumList, id: id ? Number(id) : undefined }).then(() => {
      navigate('/intelligent-platform/energyUse/plan/list');
    });
  };

  return (
    <div className={styles.container}>
      {id ? (
        <div className={styles.dateRange}>
          日期范围
          <span style={{ color: '#333333' }}>
            {dayjs(timeRange[0]).format('YYYY-MM-DD')} ～ {dayjs(timeRange[1]).format('YYYY-MM-DD')}
          </span>
        </div>
      ) : (
        <DateRange dateRange={timeRange} setDateRange={setTimeRange} limitTime={limitTime} />
      )}
      <div className={styles.actionBtn}>
        <Button
          size="small"
          className={operationType === OperationType.BatchClear ? styles.selectedBtn : ''}
          onClick={() => {
            setOperationType(OperationType.BatchClear);
            setShowBatchPopup(true);
          }}
        >
          批量清除
        </Button>
        <Button
          size="small"
          className={operationType === OperationType.BatchClear ? styles.selectedBtn : ''}
          onClick={() => {
            setOperationType(OperationType.BatchConfig);
            setShowBatchPopup(true);
          }}
        >
          批量配置
        </Button>
      </div>
      <Tabs activeKey={activeTab} onChange={key => handleTabChange(key)} className="tabs">
        {mediumList?.map(medium => (
          <Tabs.Tab title={medium.energyMediumName} key={String(medium.energyMediumId)} />
        ))}
      </Tabs>
      <div className={styles.areaContainer}>
        {!isEmpty(areaList) ? (
          areaList?.map(area => (
            <div key={area.id} className={styles.areaItem}>
              <div className={styles.areaTitle}>{area.zoneName}</div>
              <div className={styles.areaDetail}>
                {dateList.map((item, index) => {
                  // 展示当前能源介质、区域、日期的时间段
                  const mediumId =
                    activeTab || (mediumList?.[0]?.energyMediumId ? String(mediumList[0].energyMediumId) : '');
                  const areaId = String(area.id);
                  const date = item.date;
                  const detailList = areaMediumDateDetailMap?.[mediumId]?.[areaId]?.[date] || [];
                  return (
                    <div
                      className={`${styles.areaDate} ${detailList?.length > 0 ? styles.selectedArea : ''}`}
                      onClick={() => {
                        setCurrentConfigArea({ areaId, mediumId, date, detailList, orgInfo: orgInfo! });
                        if (conflictedTime.current) {
                          const curConflictedTime = conflictedTime.current.get(
                            `${mediumId}/${orgInfo?.id}/${areaId}/${date}`
                          );
                          if (curConflictedTime) {
                            setDisabledTimes(curConflictedTime);
                          }
                        }
                        setShowConfigDatePopup(true);
                      }}
                    >
                      <div key={index}>{item.date}</div>
                      {detailList.length > 0 ? (
                        detailList.map((detail, idx) => (
                          <div key={idx} style={{ fontSize: 13, color: '#999' }}>
                            {formatTimeRange(date, detail.startDateTime, detail.endDateTime)}
                          </div>
                        ))
                      ) : (
                        <div style={{ fontSize: 13, color: '#999' }}>未配置</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <Empty image={NoData} description="暂无数据" />
          </div>
        )}
      </div>

      <div className={styles.actionBar}>
        <Button size="large" onClick={() => navigate('/intelligent-platform')}>
          取消
        </Button>
        <Button
          size="large"
          color="primary"
          fill="solid"
          disabled={isEmpty(areaMediumDateDetailMap)}
          onClick={handleSubmit}
        >
          提交
        </Button>
      </div>
      <Popup
        visible={showBatchPopup}
        onClose={() => setShowBatchPopup(false)}
        position="bottom"
        className={`${styles.actionMenu}`}
      >
        {showBatchPopup && (
          <BatchConfiguration
            mediumList={mediumList}
            areaList={areaList}
            dateList={dateList}
            orgInfo={orgInfo}
            conflictedTime={conflictedTime.current}
            operationType={operationType}
            onCancel={() => setShowBatchPopup(false)}
            onOk={params => {
              setAreaMediumDateDetailMap(
                mergeWith({}, areaMediumDateDetailMap, params, (objValue, srcValue) => {
                  if (Array.isArray(srcValue)) {
                    return srcValue; // 总是用新数组覆盖
                  }
                })
              );
              setShowBatchPopup(false);
            }}
          />
        )}
      </Popup>
      <Popup
        visible={showConfigDatePopup}
        onClose={() => setShowConfigDatePopup(false)}
        position="bottom"
        className={styles.actionMenu}
      >
        {showConfigDatePopup && (
          <ConfigurationTime
            onOk={handleConfigTimeOk}
            disabledTimes={disabledTimes}
            configArea={currentConfigArea}
            onCancel={() => setShowConfigDatePopup(false)}
          />
        )}
      </Popup>
    </div>
  );
};

export default EnergyUsePlan;
